import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';

const POLL_MS = 4000;
const TOAST_TTL_MS = 12000;
const OPEN_CONVERSATION_KEY = 'crm_cw_open_conversation_id';

export default function ChatwootAssignmentToastListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const seenIdsRef = useRef(new Set());

  const pushToast = useCallback((item) => {
    const dedupeKey = String(item.id || `${item.conversation_id}-${item.created_at}`);
    if (seenIdsRef.current.has(dedupeKey)) return;
    seenIdsRef.current.add(dedupeKey);
    if (seenIdsRef.current.size > 80) {
      const keep = [...seenIdsRef.current].slice(-40);
      seenIdsRef.current = new Set(keep);
    }

    const patientName = String(item.patient_name || '').trim() || 'Sin nombre';
    const assignedBy = String(item.assigned_by || 'Administración').trim() || 'Administración';

    setToasts((prev) =>
      [
        ...prev,
        {
          id: dedupeKey,
          conversationId: Number(item.conversation_id),
          title: patientName,
          body: `Te asignaron esta conversación · ${assignedBy}`
        }
      ].slice(-5)
    );
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chatwoot/assignment-notifications`, {
          credentials: 'include'
        });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        (json?.data || []).forEach((item) => pushToast(item));
      } catch {
        // silencioso
      }
    }

    poll();
    const intervalId = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user?.id, pushToast]);

  useEffect(() => {
    if (!toasts.length) return undefined;
    const timers = toasts.map((toast) =>
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, TOAST_TTL_MS)
    );
    return () => timers.forEach((id) => clearTimeout(id));
  }, [toasts]);

  function openConversation(conversationId) {
    if (!conversationId) return;
    try {
      sessionStorage.setItem(OPEN_CONVERSATION_KEY, String(conversationId));
    } catch {
      // ignore
    }
    navigate('/conversaciones');
  }

  if (!toasts.length || typeof document === 'undefined') return null;

  return createPortal(
    <div className="crm-cw-toast-stack crm-cw-toast-stack--assign" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          className="crm-cw-toast crm-cw-toast--assign"
          onClick={() => {
            openConversation(toast.conversationId);
            setToasts((prev) => prev.filter((t) => t.id !== toast.id));
          }}
        >
          <div className="crm-cw-toast-dot" aria-hidden="true" />
          <div className="crm-cw-toast-content">
            <strong>{toast.title}</strong>
            <span>{toast.body}</span>
          </div>
        </button>
      ))}
    </div>,
    document.body
  );
}

export { OPEN_CONVERSATION_KEY };
